import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Mail } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryClient';
import { Skeleton } from '@/components/ui/skeleton';
import { relativeTime } from '@/lib/format';
import { EmailBody } from './EmailBody';

/**
 * Collapsible "Original message" card shown in the draft composer. Defaults
 * expanded for replies (the source is the whole point), collapsed for
 * forwards (the forwarded block is already in the draft body).
 */
export function OriginalMessagePanel({
  messageId,
  defaultExpanded = true,
}: {
  messageId: string;
  defaultExpanded?: boolean;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const { data, isLoading } = useQuery({
    queryKey: qk.message(messageId),
    queryFn: () => api.getMessage(messageId),
  });

  if (isLoading) return <Skeleton className="h-24 w-full" />;
  if (!data) return null;
  const m = data.message;

  return (
    <div className="rounded-md border border-border bg-muted/20">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left"
      >
        {expanded ? (
          <ChevronDown className="h-3.5 w-3.5 flex-none text-muted-foreground" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 flex-none text-muted-foreground" />
        )}
        <Mail className="h-3.5 w-3.5 flex-none text-muted-foreground" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-[13px] font-medium">
            Original message: {m.subject || '(no subject)'}
          </p>
          <p className="truncate text-[11px] text-muted-foreground">
            from {m.fromName ? `${m.fromName} <${m.fromEmail}>` : m.fromEmail} ·{' '}
            {relativeTime(m.receivedAt)}
          </p>
        </div>
      </button>
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ type: 'spring', stiffness: 120, damping: 14 }}
            className="overflow-hidden"
          >
            <div className="border-t border-border px-3 py-3">
              <EmailBody body={m.bodyPlain} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
